import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Check, X, Clock, AlertCircle, Users, Search, TrendingUp, PieChart, Eye, FileText, ExternalLink, Paperclip, MessageSquare, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export default function LeaveManagement() {
  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Iter94 (Ankur 10-Jul-2026) — HR asked for the ability to look up
  // "employees on approved leave" for a date other than today (typically
  // to plan cover / staffing for a future date). Defaults to today.
  const [onLeaveFilterDate, setOnLeaveFilterDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Detail and Review dialogs
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    days_count: 0,
    attachment_url: '',
    is_half_day: false,
    half_day_period: 'morning',
  });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager'].includes(user.role);
  const canReview = ['admin', 'hr_manager', 'department_head', 'manager'].includes(user.role);
  const isEmployee = user.role === 'employee';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLeaveBalanceLoading(true);
      const [appsRes, typesRes, holidaysRes, employeesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/leave/applications`),
        axios.get(`${API}/leave/types`),
        axios.get(`${API}/public-holidays?year=2025`),
        isHRAdmin ? axios.get(`${API}/employees`) : Promise.resolve({ data: [] }),
        // Leave balance is per-employee; fetch only for users with a linked employee_id.
        user.employee_id
          ? axios.get(`${API}/leave/balance`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);

      // Filter applications based on user role
      if (isEmployee) {
        setApplications(appsRes.data.filter(app => app.employee_id === user.employee_id));
      } else {
        setApplications(appsRes.data);
      }

      setLeaveTypes(typesRes.data);
      setPublicHolidays(holidaysRes.data);
      setEmployees(employeesRes.data);
      setLeaveBalance(Array.isArray(balanceRes.data) ? balanceRes.data : []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setLeaveBalanceLoading(false);
    }
  };

  // Calculate currently on leave (approved leaves that include today)
  const currentlyOnLeave = useMemo(() => {
    // Iter94 — Now uses `onLeaveFilterDate` (defaults to today) so HR can
    // see "who will be on leave on 25 Jul" without changing today's date.
    const filterDate = onLeaveFilterDate || new Date().toISOString().split('T')[0];
    return applications.filter(app => {
      if (app.status !== 'approved') return false;
      return app.start_date <= filterDate && app.end_date >= filterDate;
    });
  }, [applications, onLeaveFilterDate]);

  // Leave analytics by category — TWO views:
  //   - active: approved leaves whose date range includes today ("on leave today")
  //   - pending: status === 'pending', any future date (HR queue waiting for action)
  const leaveAnalytics = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const analytics = {};

    leaveTypes.forEach((type) => {
      const sameType = applications.filter(
        (app) => app.leave_type_id === type.leave_type_id,
      );
      const activeApproved = sameType.filter(
        (app) =>
          app.status === 'approved' &&
          app.start_date <= today &&
          app.end_date >= today,
      );
      const pending = sameType.filter((app) => app.status === 'pending');
      const approved = sameType.filter((app) => app.status === 'approved');

      analytics[type.leave_type_id] = {
        ...type,
        count: activeApproved.length, // legacy "on leave today" count for back-compat
        pending_count: pending.length,
        approved_count: approved.length,
        employees: activeApproved,
        pending_employees: pending,
      };
    });

    return analytics;
  }, [applications, leaveTypes]);

  // Pending leave totals across all types (HR dashboard top metric)
  const pendingTotals = useMemo(() => {
    const pending = applications.filter((app) => app.status === 'pending');
    return {
      count: pending.length,
      byType: pending.reduce((acc, app) => {
        const k = app.leave_type_id || 'OTHER';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {}),
    };
  }, [applications]);

  // Filter employees on leave for selected category.
  // When a card is selected we show the SAME slice the card counted —
  // i.e. pending applications for that leave type. Without a card selected
  // we fall back to "employees on leave today" (approved + active).
  const filteredEmployeesOnLeave = useMemo(() => {
    let source;
    if (selectedCategory) {
      source = leaveAnalytics[selectedCategory]?.pending_employees || [];
    } else {
      source = currentlyOnLeave;
    }
    let filtered = source;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => {
        const employee = employees.find(e => e.employee_id === app.employee_id);
        return (
          app.employee_name?.toLowerCase().includes(query) ||
          app.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [selectedCategory, leaveAnalytics, currentlyOnLeave, searchQuery, employees]);

  // Calculate business days excluding weekends and public holidays
  const calculateBusinessDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) return 0;
    
    let count = 0;
    const holidayDates = publicHolidays.map(h => h.date);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Skip public holidays
        if (!holidayDates.includes(dateStr)) {
          count++;
        }
      }
    }
    
    return count;
  };

  // Update days count when dates change
  useEffect(() => {
    if (formData.is_half_day && formData.start_date) {
      // Half-day always counts as 0.5 day regardless of end_date
      setFormData(prev => ({ ...prev, end_date: prev.start_date, days_count: 0.5 }));
      return;
    }
    if (formData.start_date && formData.end_date) {
      const days = calculateBusinessDays(formData.start_date, formData.end_date);
      setFormData(prev => ({ ...prev, days_count: days }));
    }
  }, [formData.start_date, formData.end_date, formData.is_half_day]);

  // TC-LV-attachment: open leave attachment with auth header + blob, never a raw href.
  const openLeaveAttachment = async (attachmentUrl) => {
    if (!attachmentUrl) return;
    try {
      const token = localStorage.getItem('hrms_token');
      // Build the full URL. If the backend ever returns an absolute http(s) URL
      // (e.g. presigned S3 in future), use it as-is. Otherwise, strip a leading
      // /api so we don't double-prefix when joining with the API base.
      const raw = String(attachmentUrl);
      const full = /^https?:\/\//i.test(raw)
        ? raw
        : `${API}${(raw.replace(/^\/?api\//, '/')).startsWith('/') ? raw.replace(/^\/?api\//, '/') : `/${raw.replace(/^\/?api\//, '/')}`}`;
      const res = await fetch(full, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        if (res.status === 401) {
          toast.error('Session expired. Please re-login.');
        } else if (res.status === 403) {
          toast.error('You are not authorized to view this attachment.');
        } else if (res.status === 404) {
          toast.error('Attachment not found.');
        } else {
          toast.error(`Failed to open attachment (status ${res.status})`);
        }
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      // Open in a new tab. Note: for blob: URLs we deliberately do NOT pass
      // 'noopener' — some browsers return null from window.open when noopener
      // is set even on success, which would trigger our download fallback
      // unnecessarily. Blob URLs are same-origin so this is safe.
      const win = window.open(objectUrl, '_blank');
      if (!win) {
        // Popup blocked — surface a download fallback
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = 'leave_attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      toast.error('Could not open attachment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ----- Front-end validation matching backend rules -----
    if (!formData.leave_type_id) {
      toast.error('Please select a leave type');
      return;
    }
    if (!formData.start_date || (!formData.end_date && !formData.is_half_day)) {
      toast.error('Start and end dates are required');
      return;
    }
    if (!(formData.reason || '').trim()) {
      toast.error('Reason is required');
      return;
    }
    // TC-LV-011: End < Start
    const startD = new Date(formData.start_date);
    const endD = new Date(formData.is_half_day ? formData.start_date : formData.end_date);
    if (endD < startD) {
      toast.error('End date cannot be before start date');
      return;
    }
    // TC-LV-006: Past dates (allow medical leave to be backdated)
    const leaveType = leaveTypes.find((t) => t.leave_type_id === formData.leave_type_id);
    const isMedical = leaveType && /medical|mc|sick/i.test(leaveType.name || '');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!isMedical && startD < today) {
      toast.error('Cannot apply leave for past dates');
      return;
    }
    // TC-LV-007: Balance check (skip unpaid + medical). Defensive: only enforce
    // when we actually have a balance record AND the user is applying for themselves.
    const isUnpaid = leaveType && /unpaid/i.test(leaveType.name || '');
    const applyingForSelf = !isHRAdmin || formData.employee_id === user.employee_id;
    if (!isUnpaid && !isMedical && applyingForSelf) {
      const safeBalances = Array.isArray(leaveBalance) ? leaveBalance : [];
      const lt = safeBalances.find((b) => b && b.leave_type_id === formData.leave_type_id);
      if (lt && Number(formData.days_count) > Number(lt.balance)) {
        toast.error(
          `Insufficient balance: you have ${lt.balance} day(s) of ${lt.name} but requested ${formData.days_count}.`,
        );
        return;
      }
    }
    if (formData.days_count === 0) {
      toast.error('Please select valid dates (excluding weekends and holidays)');
      return;
    }
    
    try {
      // TC-LV-004: Upload MC attachment first if present
      let attachmentUrl = formData.attachment_url || '';
      if (attachmentFile) {
        setAttachmentUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', attachmentFile);
          const upRes = await axios.post(`${API}/leave/upload-attachment`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          attachmentUrl = upRes.data?.attachment_url || '';
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Failed to upload attachment');
          setAttachmentUploading(false);
          return;
        } finally {
          setAttachmentUploading(false);
        }
      }

      const payload = {
        ...formData,
        attachment_url: attachmentUrl,
      };
      const submitResp = await axios.post(`${API}/leave/applications`, payload);
      const warning = submitResp.headers?.['x-leave-warning'];
      if (warning) {
        // TC-LV-012: long-leave warning surfaced to the user
        toast.warning(warning, { duration: 6000 });
      }
      // Iter72 — TC-LV-032: explicit notification to user that emails have
      // been dispatched to them and their reporting manager. Without this
      // toast, testers thought no emails were sent (they were — verified
      // on Resend dashboard — but at @myhrorbit.com test addresses).
      toast.success('Leave application submitted. Notification email sent to you and your reporting manager.', { duration: 5000 });
      setDialogOpen(false);
      setFormData({
        leave_type_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0,
        attachment_url: '',
        is_half_day: false,
        half_day_period: 'morning',
      });
      setAttachmentFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleReview = async (leaveId, action) => {
    try {
      await axios.put(`${API}/leave/applications/${leaveId}/review`, { action });
      // Iter72 — TC-LV-033/034: surface the email notification that fires
      // server-side so HR/Manager can SEE the email was sent (rather than
      // assuming it was missed).
      toast.success(
        `Leave ${action === 'approve' ? 'approved' : 'rejected'}. Notification email sent to the employee.`,
        { duration: 5000 }
      );
      fetchData();
    } catch (error) {
      // Iter80 (Ankur 07-Jul-2026) — surface the backend error detail
      // instead of swallowing it as "Failed to review leave". Reject
      // callers that don't route through the dialog (legacy or
      // programmatic invocations) will now get the actionable
      // "Please provide a reason for rejection" message directly.
      toast.error(error.response?.data?.detail || 'Failed to review leave');
    }
  };

  // TC-LV-015: Cancel my own pending leave application
  const handleCancelLeave = async (leaveId) => {
    if (!window.confirm('Cancel this pending leave application?')) return;
    try {
      await axios.delete(`${API}/leave/applications/${leaveId}`);
      toast.success('Leave application cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  // Open leave detail modal
  const openLeaveDetail = (leave) => {
    setSelectedLeave(leave);
    setDetailDialogOpen(true);
  };

  // Open review dialog with action
  const openReviewDialog = (leave, action) => {
    setSelectedLeave(leave);
    setReviewAction(action);
    setReviewRemarks('');
    setReviewDialogOpen(true);
  };

  // Submit review with remarks
  const submitReview = async () => {
    if (!selectedLeave || !reviewAction) return;
    
    // Require remarks for rejection
    if (reviewAction === 'reject' && !reviewRemarks.trim()) {
      toast.error('Please provide remarks for rejection');
      return;
    }
    
    setReviewLoading(true);
    try {
      await axios.put(`${API}/leave/applications/${selectedLeave.leave_id}/review`, { 
        action: reviewAction,
        remarks: reviewRemarks.trim() || null
      });
      
      toast.success(
        `Leave ${reviewAction === 'approve' ? 'approved' : 'rejected'} successfully. Notification email sent to the employee.`,
        { duration: 5000 }
      );
      setReviewDialogOpen(false);
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${reviewAction} leave`);
    } finally {
      setReviewLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (selectedTab === 'all') return true;
    return app.status === selectedTab;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
      approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // HR Admin Analytics View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Leave Analytics</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor staff leave status and patterns</p>
          </div>
          {/* TC-LV-001 / TC-LV-002 / TC-LV-004: HR Admin can apply leave on behalf */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600"
                data-testid="apply-leave-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Apply Leave (On Behalf)
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
              </DialogHeader>
              <ApplyLeaveForm
                leaveTypes={leaveTypes}
                leaveBalance={leaveBalance}
                publicHolidays={publicHolidays}
                employees={employees}
                isHRAdmin={isHRAdmin}
                formData={formData}
                setFormData={setFormData}
                attachmentFile={attachmentFile}
                setAttachmentFile={setAttachmentFile}
                attachmentUploading={attachmentUploading}
                onSubmit={handleSubmit}
                onCancel={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* HR Summary Cards — Currently On Leave + Pending Queue */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="leave-hr-summary">
          {/* Currently On Leave */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="h-6 w-6" />
                  <span className="text-blue-100 font-medium">Currently On Leave</span>
                </div>
                <div className="text-4xl font-bold" data-testid="currently-on-leave-count">{currentlyOnLeave.length}</div>
                <p className="text-blue-100 text-sm mt-1">approved leaves active today</p>
              </div>
              <div className="text-right">
                <PieChart className="h-16 w-16 text-blue-200 opacity-50" />
              </div>
            </div>
          </div>
          {/* Pending Applications */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="h-6 w-6" />
                  <span className="text-amber-100 font-medium">Pending Applications</span>
                </div>
                <div className="text-4xl font-bold" data-testid="pending-leave-total">{pendingTotals.count}</div>
                <p className="text-amber-100 text-sm mt-1">awaiting approval</p>
              </div>
              <div className="text-right">
                <ClipboardList className="h-16 w-16 text-amber-200 opacity-50" />
              </div>
            </div>
          </div>
        </div>

        {/* Leave Categories Analytics — pending count + on-leave-today */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Leave by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {leaveTypes.map(type => {
              const analytics = leaveAnalytics[type.leave_type_id];
              const isSelected = selectedCategory === type.leave_type_id;
              const pendingCount = analytics?.pending_count || 0;
              const activeCount = analytics?.count || 0;
              return (
                <button
                  key={type.leave_type_id}
                  data-testid={`leave-category-${type.leave_type_id}`}
                  onClick={() => setSelectedCategory(isSelected ? null : type.leave_type_id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${type.color}20` }}
                  >
                    <CalendarIcon className="h-5 w-5" style={{ color: type.color }} />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{type.name}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{pendingCount}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">pending</p>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {activeCount} on leave today
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Employee List */}
        {(selectedCategory || currentlyOnLeave.length > 0 || pendingTotals.count > 0) && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid="leave-list-heading">
                {selectedCategory
                  ? `${leaveTypes.find(t => t.leave_type_id === selectedCategory)?.name} — Pending Applications`
                  : `All Employees on Approved Leave (${onLeaveFilterDate === new Date().toISOString().split('T')[0] ? 'Today' : onLeaveFilterDate})`
                }
                <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400" data-testid="leave-list-count">
                  ({filteredEmployeesOnLeave.length})
                </span>
              </h3>
              <div className="flex items-center gap-3">
                {/* Iter94 — HR-visible date picker so they can pre-plan
                    staffing coverage for future/past dates. Hidden when
                    a specific leave-type category is selected (pending
                    list is date-agnostic). */}
                {!selectedCategory && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 dark:text-slate-400">Show for date:</label>
                    <Input
                      type="date"
                      value={onLeaveFilterDate}
                      onChange={(e) => setOnLeaveFilterDate(e.target.value || new Date().toISOString().split('T')[0])}
                      className="h-8 w-40 text-sm dark:bg-slate-900 dark:border-slate-600"
                      data-testid="leave-list-date-filter"
                    />
                    {onLeaveFilterDate !== new Date().toISOString().split('T')[0] && (
                      <button
                        onClick={() => setOnLeaveFilterDate(new Date().toISOString().split('T')[0])}
                        className="text-xs text-blue-600 hover:text-blue-700"
                        data-testid="leave-list-date-today-btn"
                      >
                        Today
                      </button>
                    )}
                  </div>
                )}
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`text-sm text-blue-600 hover:text-blue-700 ${!selectedCategory && 'hidden'}`}
                >
                  Show All
                </button>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>

            {/* Employee List */}
            {filteredEmployeesOnLeave.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">
                  {searchQuery
                    ? 'No employees found matching your search'
                    : selectedCategory
                      ? 'No pending applications in this category'
                      : `No employees on approved leave on ${onLeaveFilterDate === new Date().toISOString().split('T')[0] ? 'today' : onLeaveFilterDate}`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Leave Type</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Duration</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Days</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">{selectedCategory ? 'Action' : 'Return Date'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredEmployeesOnLeave.map(app => {
                      const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                      const employee = employees.find(e => e.employee_id === app.employee_id);
                      const returnDate = new Date(app.end_date);
                      returnDate.setDate(returnDate.getDate() + 1);
                      
                      return (
                        <tr key={app.leave_id || app.application_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {(app.employee_name || 'U').charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                                {employee?.email && (
                                  <p className="text-xs text-slate-400 dark:text-slate-500">{employee.email}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span 
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}
                            >
                              {leaveType?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                            {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{app.days_count} days</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              app.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : app.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300'
                            }`}>
                              {app.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                            {selectedCategory ? (
                              <button
                                type="button"
                                onClick={() => openLeaveDetail(app)}
                                className="px-3 py-1 rounded-lg text-xs font-medium border border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                                data-testid={`review-pending-${app.leave_id || app.application_id}`}
                              >
                                Review
                              </button>
                            ) : (
                              returnDate.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pending Applications for Review */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pending Leave Applications</h3>
            <span className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium">
              {applications.filter(a => a.status === 'pending').length} pending
            </span>
          </div>
          
          {applications.filter(a => a.status === 'pending').length === 0 ? (
            <div className="text-center py-8">
              <Check className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">No pending applications to review</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Leave Type</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Duration</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Reason</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Attachment</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {applications.filter(a => a.status === 'pending').map(app => {
                    const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                    return (
                      <tr key={app.leave_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}
                          >
                            {leaveType?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                          <span className="ml-2 text-slate-500">({app.days_count} days)</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                          {app.reason || 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          {app.attachment_url ? (
                            <button
                              type="button"
                              onClick={() => openLeaveAttachment(app.attachment_url)}
                              data-testid={`leave-attachment-view-${app.leave_id}`}
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm"
                            >
                              <Paperclip className="h-4 w-4 mr-1" />
                              View
                            </button>
                          ) : (
                            <span className="text-slate-400 text-sm">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openLeaveDetail(app)}
                              className="h-8"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openReviewDialog(app, 'approve')}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openReviewDialog(app, 'reject')}
                              className="h-8"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leave Detail Modal */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-blue-500" />
                Leave Application Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedLeave && (
              <div className="space-y-6">
                {/* Employee & Leave Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Employee</label>
                    <p className="text-slate-900 dark:text-slate-100 font-medium">{selectedLeave.employee_name || 'N/A'}</p>
                    <p className="text-xs text-slate-500">{selectedLeave.employee_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Leave Type</label>
                    <p className="text-slate-900 dark:text-slate-100 font-medium">
                      {leaveTypes.find(lt => lt.leave_type_id === selectedLeave.leave_type_id)?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Duration</label>
                    <p className="text-slate-900 dark:text-slate-100">
                      {new Date(selectedLeave.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {' - '}
                      {new Date(selectedLeave.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Days</label>
                    <p className="text-2xl font-bold text-blue-600">{selectedLeave.days_count} days</p>
                  </div>
                </div>
                
                {/* Reason */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Reason</label>
                  <p className="text-slate-900 dark:text-slate-100 mt-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    {selectedLeave.reason || 'No reason provided'}
                  </p>
                </div>
                
                {/* Attachment */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Supporting Documents
                  </label>
                  <div className="mt-2">
                    {selectedLeave.attachment_url ? (
                      <button
                        type="button"
                        onClick={() => openLeaveAttachment(selectedLeave.attachment_url)}
                        data-testid="leave-detail-attachment-btn"
                        className="w-full text-left flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <FileText className="h-5 w-5 text-blue-500" />
                        <span className="text-slate-900 dark:text-slate-100 flex-1">Medical Certificate / Supporting Document</span>
                        <ExternalLink className="h-4 w-4 text-slate-400" />
                      </button>
                    ) : (
                      <p className="text-slate-500 dark:text-slate-400 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        No documents attached
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Status</label>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getStatusBadge(selectedLeave.status)}`}>
                      {selectedLeave.status}
                    </span>
                    {selectedLeave.reviewer_remarks && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm font-medium text-red-800 dark:text-red-300">Rejection Remarks:</p>
                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">{selectedLeave.reviewer_remarks}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                {selectedLeave.status === 'pending' && canReview && (
                  <DialogFooter className="flex gap-2">
                    <Button 
                      onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedLeave, 'approve'); }}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="h-4 w-4 mr-2" />Approve
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedLeave, 'reject'); }}
                    >
                      <X className="h-4 w-4 mr-2" />Reject
                    </Button>
                    <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                      Close
                    </Button>
                  </DialogFooter>
                )}
                {(selectedLeave.status !== 'pending' || !canReview) && (
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                      Close
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Review Dialog with Remarks */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
                {reviewAction === 'approve' && <Check className="h-5 w-5 text-emerald-500" />}
                {reviewAction === 'reject' && <X className="h-5 w-5 text-red-500" />}
                {reviewAction === 'approve' ? 'Approve Leave' : 'Reject Leave'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedLeave && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Employee:</strong> {selectedLeave.employee_name}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Leave:</strong> {leaveTypes.find(lt => lt.leave_type_id === selectedLeave.leave_type_id)?.name} ({selectedLeave.days_count} days)
                  </p>
                </div>
              )}
              
              <div>
                <Label className="dark:text-slate-100">
                  {reviewAction === 'reject' ? 'Rejection Remarks (Required)' : 'Remarks (Optional)'}
                </Label>
                <Textarea
                  value={reviewRemarks}
                  onChange={(e) => setReviewRemarks(e.target.value)}
                  placeholder={
                    reviewAction === 'reject' ? 'Please provide reason for rejection...' :
                    'Add any comments...'
                  }
                  className="mt-2 min-h-[100px] dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitReview}
                  disabled={reviewLoading || (reviewAction === 'reject' && !reviewRemarks.trim())}
                  className={reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                >
                  {reviewLoading ? 'Processing...' : reviewAction === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Leave Management</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">{applications.length} total applications</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600"
              data-testid="apply-leave-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
            </DialogHeader>
            <ApplyLeaveForm
              leaveTypes={leaveTypes}
              leaveBalance={leaveBalance}
              publicHolidays={publicHolidays}
              employees={employees}
              isHRAdmin={isHRAdmin}
              formData={formData}
              setFormData={setFormData}
              attachmentFile={attachmentFile}
              setAttachmentFile={setAttachmentFile}
              attachmentUploading={attachmentUploading}
              onSubmit={handleSubmit}
              onCancel={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200 dark:border-slate-700">
        {['all', 'pending', 'approved', 'rejected'].map(tab => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              selectedTab === tab
                ? 'text-slate-900 dark:text-slate-100 border-b-2 border-slate-900 dark:border-slate-100'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab} ({applications.filter(a => tab === 'all' ? true : a.status === tab).length})
          </button>
        ))}
      </div>

      {/* Applications List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm">
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">No leave applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Leave Type</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Duration</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Days</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                  <th className="text-center px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map(app => {
                  const leaveType = leaveTypes.find(lt => lt.leave_type_id === app.leave_type_id);
                  const isMyApp = app.employee_id === user?.employee_id;
                  return (
                    <tr 
                      key={app.leave_id} 
                      className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                      onClick={() => {
                        toast.info(`Leave Application: ${app.employee_name || 'Employee'}\n${leaveType?.name || 'Leave'}: ${app.days_count} days\nStatus: ${app.status}\nReason: ${app.reason || 'N/A'}`);
                      }}
                      title="Click to view details"
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{app.employee_name || 'N/A'}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{app.employee_id}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${leaveType?.color}20`, color: leaveType?.color }}>
                          {leaveType?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                        {new Date(app.start_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} - {new Date(app.end_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {/* Iter72 (Ankur 06-Jul-2026) — half-day leaves were
                            showing "0 days" (int(0.5) rounding). Preserve the
                            .5 explicitly when is_half_day is true. */}
                        {(() => {
                          const raw = Number(app.days_count ?? 0);
                          if (app.is_half_day && raw < 1) return '0.5 days';
                          if (Number.isInteger(raw)) return `${raw} days`;
                          return `${raw} days`;
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      {/* TC-LV-015: Cancel button for the applicant's own pending leaves, plus
                          existing Approve/Reject for reviewers */}
                      <td className="px-6 py-4 text-center">
                        {app.status === 'pending' && canReview && (
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleReview(app.leave_id, 'approve'); }}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                              data-testid={`approve-leave-${app.leave_id}`}
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => { e.stopPropagation(); openReviewDialog(app, 'reject'); }}
                              className="h-8"
                              data-testid={`reject-leave-${app.leave_id}`}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {app.status === 'pending' && !canReview && isMyApp && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleCancelLeave(app.leave_id); }}
                            className="h-8 text-red-600 hover:text-red-700 border-red-200"
                            data-testid={`cancel-leave-${app.leave_id}`}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        )}
                        {/* Iter72 (Ankur 06-Jul-2026) — HR/Admin needs a way
                            to revoke an already-approved leave (e.g. converting
                            an approved half-day to a full-day). The backend
                            DELETE /api/leave/applications/{id} already supports
                            this and refunds balance; just needed the UI. */}
                        {app.status === 'approved' && canReview && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Revoke this approved leave? The employee\'s balance will be refunded and they can then re-apply.')) {
                                handleCancelLeave(app.leave_id);
                              }
                            }}
                            className="h-8 text-red-600 hover:text-red-700 border-red-200"
                            data-testid={`revoke-leave-${app.leave_id}`}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Iter84 (Shikha 08-Jul-2026) — Review Dialog for the
          Manager/Employee tree. Previously only the HR/Admin view
          rendered `reviewDialogOpen`, so a Manager clicking the
          pending-list Reject button silently set state with no dialog
          to open → button "not clickable". Re-using the same
          reviewDialogOpen/setReviewDialogOpen/submitReview wiring as
          the HR view (line 939) so behaviour is identical. */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              {reviewAction === 'approve' && <Check className="h-5 w-5 text-emerald-500" />}
              {reviewAction === 'reject' && <X className="h-5 w-5 text-red-500" />}
              {reviewAction === 'approve' ? 'Approve Leave' : 'Reject Leave'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedLeave && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Employee:</strong> {selectedLeave.employee_name}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Leave:</strong> {leaveTypes.find(lt => lt.leave_type_id === selectedLeave.leave_type_id)?.name} ({selectedLeave.days_count} days)
                </p>
              </div>
            )}
            <div>
              <Label className="dark:text-slate-100">
                {reviewAction === 'reject' ? 'Rejection Remarks (Required)' : 'Remarks (Optional)'}
              </Label>
              <Textarea
                value={reviewRemarks}
                onChange={(e) => setReviewRemarks(e.target.value)}
                placeholder={reviewAction === 'reject' ? 'Please provide reason for rejection...' : 'Add any comments...'}
                className="mt-2 min-h-[100px] dark:bg-slate-900 dark:border-slate-600"
                data-testid="review-remarks-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)} data-testid="review-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={submitReview}
                disabled={reviewLoading || (reviewAction === 'reject' && !reviewRemarks.trim())}
                className={reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="review-submit-btn"
              >
                {reviewLoading ? 'Processing...' : reviewAction === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Shared Apply Leave Form — used by both HR Admin (Apply On Behalf) and Employee views.
function ApplyLeaveForm({
  leaveTypes,
  leaveBalance,
  publicHolidays,
  employees,
  isHRAdmin,
  formData,
  setFormData,
  attachmentFile,
  setAttachmentFile,
  attachmentUploading,
  onSubmit,
  onCancel,
}) {
  const selectedType = leaveTypes.find((t) => t.leave_type_id === formData.leave_type_id);
  const isMedical = selectedType && /medical|mc|sick/i.test(selectedType.name || '');
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="apply-leave-form">
      {isHRAdmin && (
        <div>
          <Label>Apply on behalf of *</Label>
          <select
            required
            value={formData.employee_id || ''}
            onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
            className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
            data-testid="apply-leave-employee-select"
          >
            <option value="">Select employee</option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.full_name} ({emp.employee_id})
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <Label>Leave Type *</Label>
        <select
          required
          value={formData.leave_type_id}
          onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
          className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100"
          data-testid="leave-type-select"
        >
          <option value="">Select leave type</option>
          {leaveTypes.map((type) => {
            // Iter88s — show REMAINING balance (not annual entitlement) so
            // the dropdown matches the dashboard "Leave Balance" widget.
            // Reported by Shikha 16-Jun-2026: dashboard showed 13 but
            // dropdown said "14 days/year", confusing testers.
            const bal = (leaveBalance || []).find(b => b.leave_type_id === type.leave_type_id);
            const remaining = bal ? Number(bal.balance ?? 0) : Number(type.days_per_year ?? 0);
            const entitled = Number(type.days_per_year ?? 0);
            const label = remaining === entitled
              ? `${type.name} — ${remaining} day(s) remaining`
              : `${type.name} — ${remaining} of ${entitled} day(s) remaining`;
            return (
              <option key={type.leave_type_id} value={type.leave_type_id}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      {/* TC-LV-002: Half-day option */}
      <div className="flex items-center gap-2 p-2 border border-slate-200 dark:border-slate-700 rounded-md">
        <input
          id="half-day-toggle"
          type="checkbox"
          checked={formData.is_half_day}
          onChange={(e) => setFormData({ ...formData, is_half_day: e.target.checked })}
          data-testid="half-day-checkbox"
          className="h-4 w-4 rounded border-slate-300"
        />
        <Label htmlFor="half-day-toggle" className="mb-0 cursor-pointer">
          Half-day leave
        </Label>
        {formData.is_half_day && (
          <select
            value={formData.half_day_period}
            onChange={(e) => setFormData({ ...formData, half_day_period: e.target.value })}
            className="ml-auto h-9 rounded-md border border-slate-300 dark:border-slate-600 px-2 bg-white dark:bg-slate-900 dark:text-slate-100 text-sm"
            data-testid="half-day-period-select"
          >
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
          </select>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Date *</Label>
          <Input
            type="date"
            required
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className="dark:bg-slate-900 dark:border-slate-600"
            data-testid="leave-start-date"
          />
        </div>
        <div>
          <Label>End Date *</Label>
          <Input
            type="date"
            required={!formData.is_half_day}
            value={formData.end_date}
            min={formData.start_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            disabled={formData.is_half_day}
            className="dark:bg-slate-900 dark:border-slate-600 disabled:opacity-60"
            data-testid="leave-end-date"
          />
        </div>
      </div>

      {formData.start_date && (formData.end_date || formData.is_half_day) && (() => {
        // Iter88w — Surface which calendar days are excluded so users
        // immediately understand "2 days" for a 3-day range.
        const excluded = [];
        if (!formData.is_half_day && formData.start_date && formData.end_date) {
          const holidayDates = (publicHolidays || []).map(h => h.date);
          const start = new Date(formData.start_date);
          const end = new Date(formData.end_date);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dow = d.getDay();
            const ds = d.toISOString().split('T')[0];
            if (dow === 0 || dow === 6) excluded.push({ date: ds, reason: 'Weekend' });
            else if (holidayDates.includes(ds)) excluded.push({ date: ds, reason: 'Public Holiday' });
          }
        }
        return (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-900 dark:text-blue-300 font-medium">
                Total: {formData.days_count} business day{formData.days_count !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
              (Excluding weekends and public holidays)
            </p>
            {excluded.length > 0 && (
              <div
                data-testid="leave-excluded-dates"
                className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300"
              >
                <strong>Excluded:</strong>{' '}
                {excluded.map((x, i) => (
                  <span key={x.date}>
                    {i > 0 && ', '}
                    {x.date} ({x.reason})
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div>
        <Label>Reason *</Label>
        <Textarea
          required
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          placeholder="Provide reason for leave..."
          className="dark:bg-slate-900 dark:border-slate-600"
          rows={3}
          data-testid="leave-reason"
        />
      </div>

      {/* TC-LV-004: MC attachment (shown for medical leave OR if user already attached) */}
      {(isMedical || formData.attachment_url || attachmentFile) && (
        <div>
          <Label>
            Supporting Document {isMedical ? '(MC Recommended)' : ''}
          </Label>
          <Input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx"
            onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
            data-testid="leave-attachment-input"
            className="dark:bg-slate-900 dark:border-slate-600"
          />
          {attachmentFile && (
            <p className="text-xs text-slate-600 mt-1">
              {attachmentFile.name} ({Math.round(attachmentFile.size / 1024)} KB)
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="bg-slate-900 hover:bg-slate-800"
          disabled={attachmentUploading}
          data-testid="leave-submit-btn"
        >
          {attachmentUploading ? 'Uploading...' : 'Submit Application'}
        </Button>
      </div>
    </form>
  );
}
